<template>
  <div class="exp-calculator">
    <h1>經驗計算機</h1>
    
    <div class="calculator-container">
      <!-- 輸入區域 -->
      <div class="input-section">
        <div class="input-group">
          <h2>當前等級</h2>
          <div class="level-input-container">
            <div class="level-input">
              <input 
                type="number" 
                v-model.number="currentLevel" 
                min="200"
                max="300"
                placeholder="請輸入當前等級"
                @input="validateInput('currentLevel')"
                @blur="validateLevelOnBlur"
                step="1"
              >
              <div class="error-message" v-if="errors.currentLevel">
                最低使用等級200
              </div>
            </div>
            <div class="percentage-input">
              <input 
                type="number" 
                v-model.number="currentExpPercentageInput" 
                min="0"
                max="100"
                step="0.001"
                placeholder="經驗值%"
                @input="updateExpFromPercentage"
              >
              <span class="percentage-symbol">%</span>
            </div>
          </div>
        </div>

        <div class="input-group">
          <h2>當前經驗值進度</h2>
          <div class="exp-progress-container">
            <div class="exp-progress-bar">
              <div class="exp-progress-fill" :style="{ width: `${currentExpPercentage}%` }"></div>
            </div>
            <div class="exp-progress-info">
              <div class="exp-percentage">{{ currentExpPercentage.toFixed(3) }}%</div>
              <div class="exp-values">
                <input 
                  type="text" 
                  v-model="currentExpValue" 
                  @input="updateExpFromValue"
                  class="exp-value-input"
                >
                /
                <span class="total-exp">{{ formattedLevelExp }}</span>
              </div>
            </div>
            <div class="burning-buttons">
              <div class="burning-left">
                <div v-if="currentLevel < 260" class="burning-controls">
                  <button 
                    @click="setBurningMode('burning1+2')" 
                    class="burning-button"
                    :class="{ active: burningMode === 'burning1+2' }"
                  >燃燒1+2</button>
                  <button 
                    @click="setBurningMode('burning1+4')" 
                    class="burning-button"
                    :class="{ active: burningMode === 'burning1+4' }"
                  >燃燒1+4</button>
                </div>
                <div v-if="currentLevel < 260" class="burning-notice">
                  ※請確認燃燒角色是否能使用經驗秘藥
                </div>
              </div>
              <div class="burning-right">
                <button @click="resetLevel" class="reset-level-button">重設等級</button>
              </div>
            </div>

          </div>
        </div>

        <div class="input-group">
          <div class="exp-tabs">
            <button 
              class="tab-button" 
              :class="{ active: activeTab === 'potion' }"
              @click="activeTab = 'potion'"
            >
              經驗秘藥
            </button>
            <button 
              class="tab-button" 
              :class="{ active: activeTab === 'ticket' }"
              @click="activeTab = 'ticket'"
            >
              EXP 交換券
            </button>
          </div>
          
          <div v-if="activeTab === 'potion'" class="potion-list">
            <div class="potion-item" v-for="(potion, index) in potions" :key="index">
              <div class="potion-info">
                <img 
                  :src="potion.maxLevel === 200 ? require('@/assets/water/200.png') : require(`@/assets/water/${potion.maxLevel}.png`)" 
                  :alt="potion.name"
                  class="potion-image"
                >
                <span class="potion-name">{{ potion.name }}</span>
              </div>
              <div class="potion-input">
                <button 
                  class="potion-btn" 
                  @click="decreasePotion(index)"
                  :disabled="potion.count === 0 || !currentLevel"
                  :class="{ 'disabled': potion.count === 0 || !currentLevel }"
                >-</button>
                <input 
                  type="number" 
                  v-model.number="potion.count" 
                  min="0"
                  placeholder="請輸入數量"
                  @input="validatePotionCount(index)"
                  :disabled="!currentLevel"
                >
                <button 
                  class="potion-btn" 
                  @click="increasePotion(index)"
                  :disabled="!currentLevel"
                  :class="{ 'disabled': !currentLevel }"
                >+</button>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'ticket'" class="ticket-list">
            <div class="ticket-item" v-for="(ticket, index) in tickets" :key="index">
              <div class="ticket-info">
                <img 
                  :src="require(`@/assets/${ticket.image}`)" 
                  :alt="ticket.name"
                  class="ticket-image"
                >
                <div class="ticket-details">
                  <span class="ticket-name">{{ ticket.name }}</span>
                  <span class="ticket-level-range">{{ ticket.levelRange }}</span>
                </div>
              </div>
              <div class="ticket-input-container">
                <div v-if="!canUseTicket(ticket.type)" class="ticket-error">
                  當前等級無法使用此票券
                </div>
                <div class="ticket-input">
                  <input 
                    type="number" 
                    v-model.number="ticket.count" 
                    min="0"
                    placeholder="請輸入數量"
                    @input="validateTicketCount(index)"
                    :disabled="!currentLevel || !canUseTicket(ticket.type)"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="button-group">
          <button 
            @click="calculateResult" 
            class="calculate-button"
            :disabled="!hasPotions"
            :class="{ 'disabled': !hasPotions }"
          >提升</button>
          <button 
            @click="resetPotions" 
            class="reset-button"
            :disabled="!hasPotions"
            :class="{ 'disabled': !hasPotions }"
          >重設</button>
        </div>

        <div v-if="showResult" class="result-section">
          <div class="subtext">下方為預覽結果，可調整秘藥/票券後按【提升】，重新預覽<br>
            按【確定提升】會將預覽結果直接反映至當前等級
          </div>
          <h2>提升結果預覽</h2>
          <div class="result-content">
            <div class="result-item">
              <span class="result-value">{{ resultLevel }}</span>
            </div>
            <div class="result-item">
              <div class="exp-progress-container">
                <div class="exp-progress-bar">
                  <div class="exp-progress-fill" :style="{ width: `${resultPercentage}%` }"></div>
                </div>
                <div class="exp-progress-info">
                  <div class="exp-percentage">{{ resultPercentage.toFixed(3) }}%</div>
                  <div class="exp-values">
                    <span class="current-exp">{{ formattedResultExp }}</span>
                    /
                    <span class="total-exp">{{ formattedResultLevelExp }}</span>
                  </div>
                </div>
              </div>
            </div>
            <button @click="confirmUpgrade" class="calculate-button">確定提升</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { potions } from '@/data/expData'
import { ExpCalculator } from '@/utils/ExpCalculator'

export default {
  name: 'ExpCalculator',
  data() {
    return {
      calculator: new ExpCalculator(),
      currentLevel: null,
      currentExpValue: '',
      currentExpPercentageInput: null,
      errors: {
        currentLevel: false
      },
      potions: [...potions],
      showResult: false,
      resultLevel: null,
      resultExp: 0,
      resultPercentage: 0,
      resultLevelExp: 0,
      activeTab: 'potion',
      burningMode: null,
      tickets: [
        { 
          name: 'EXP 交換券', 
          levelRange: '200-299', 
          image: 'exp.png', 
          type: 'expTicket', 
          count: 0 
        },
        { 
          name: '上級 EXP 交換券', 
          levelRange: '260-299', 
          image: 'expplus.png', 
          type: 'expPlusTicket', 
          count: 0 
        }
      ]
    }
  },
  created() {
    this.loadFromLocalStorage()
  },
  computed: {
    currentLevelExp() {
      return this.calculator.getLevelExp(this.currentLevel)
    },
    formattedLevelExp() {
      return this.calculator.formatNumber(this.currentLevelExp)
    },
    formattedResultExp() {
      return this.calculator.formatNumber(this.resultExp)
    },
    formattedResultLevelExp() {
      return this.calculator.formatNumber(this.resultLevelExp)
    },
    currentExpPercentage() {
      return this.calculator.calculateExpPercentage(this.currentLevel, this.currentExpValue)
    },
    hasPotions() {
      const hasPotions = this.currentLevel && this.potions.some(potion => potion.count > 0)
      const hasTickets = this.currentLevel && this.tickets.some(ticket => ticket.count > 0)
      return hasPotions || hasTickets
    },
    isInputValid() {
      return this.currentLevel && !this.errors.currentLevel
    }
  },
  watch: {
    currentLevel() {
      this.saveToLocalStorage()
      if (this.currentLevel >= 260) {
        this.burningMode = null
      }
    },
    currentExpValue() {
      this.saveToLocalStorage()
    },
    currentExpPercentageInput() {
      this.saveToLocalStorage()
    }
  },
  methods: {
    validateInput(field) {
      const value = this[field]
      if (value === null || value === '') {
        this.errors[field] = false
        return
      }

      if (field === 'currentLevel') {
        // 使用 ExpCalculator 的 sanitizeLevelInput 處理
        this.currentLevel = this.calculator.sanitizeLevelInput(this.currentLevel)
        // 當等級變動時，經驗值%與經驗值數值都清空
        this.currentExpPercentageInput = null
        this.currentExpValue = ''
        this.errors[field] = false
      }
      if (field === 'currentExpPercentageInput') {
        // 確保百分比在有效範圍內
        this.currentExpPercentageInput = Math.min(Math.max(this.currentExpPercentageInput || 0, 0), 100)
        this.updateExpFromPercentage()
      }
    },
    validateLevelOnBlur() {
      if (this.currentLevel !== null && this.currentLevel !== '') {
        this.errors.currentLevel = !this.calculator.validateLevel(this.currentLevel)
        if (this.errors.currentLevel) {
          this.currentLevel = null
        }
      }
    },
    updateExpFromValue() {
      if (!this.currentLevel) return
      
      // 確保是字串類型
      const inputValue = String(this.currentExpValue || '')
      
      // 移除所有非數字和逗號的字符
      let cleanExp = inputValue.replace(/[^\d,]/g, '').replace(/,/g, '')
      if (!cleanExp) {
        this.currentExpValue = '0'
        this.currentExpPercentageInput = 0
        return
      }

      const result = this.calculator.validateAndFormatExp(this.currentLevel, cleanExp)
      this.currentExpValue = result.formattedExp
      this.currentExpPercentageInput = parseFloat(result.percentage.toFixed(3))
    },
    updateExpFromPercentage() {
      if (!this.currentLevel || this.currentExpPercentageInput === null) return
      
      // 確保百分比是數字類型
      let percentage = parseFloat(this.currentExpPercentageInput)
      if (isNaN(percentage)) {
        percentage = 0
      }
      
      // 確保百分比在有效範圍內
      let validPercentage = Math.min(Math.max(percentage, 0), 100)
      
      const result = this.calculator.validateAndFormatPercentage(this.currentLevel, validPercentage)
      this.currentExpValue = result.formattedExp
      this.currentExpPercentageInput = parseFloat(result.percentage.toFixed(3))
    },
    validatePotionCount(index) {
      const potion = this.potions[index]
      potion.count = this.calculator.validatePotionCount(potion.count)
    },
    increasePotion(index) {
      this.potions[index].count = this.calculator.increaseCount(this.potions[index].count)
    },
    decreasePotion(index) {
      this.potions[index].count = this.calculator.decreaseCount(this.potions[index].count)
    },
    calculateResult() {
      if (!this.isInputValid) return

      if (this.currentExpPercentageInput === null) {
        this.currentExpPercentageInput = 0
        this.updateExpFromPercentage()
      }

      if (!this.currentExpValue) {
        this.currentExpValue = '0'
      }

      // 確保經驗值是字串類型
      const expValue = String(this.currentExpValue)

      let result = null

      if (this.activeTab === 'potion') {
        result = this.calculator.calculateResult(this.currentLevel, expValue, this.potions, this.burningMode)
      } else if (this.activeTab === 'ticket') {
        const tickets = {
          expTicket: this.tickets[0].count,
          expPlusTicket: this.tickets[1].count
        }
        result = this.calculator.calculateTicketResult(this.currentLevel, expValue, tickets, this.burningMode)
      }

      if (result) {
        this.resultLevel = result.level
        this.resultExp = result.exp
        this.resultLevelExp = result.levelExp
        this.resultPercentage = result.percentage
        this.showResult = true

        this.$nextTick(() => {
          const resultSection = document.querySelector('.result-section')
          if (resultSection) {
            resultSection.scrollIntoView({ behavior: 'smooth' })
          }
        })
      }
    },
    resetLevel() {
      this.currentLevel = null
      this.currentExpValue = ''
      this.currentExpPercentageInput = null
      this.potions.forEach(potion => {
        potion.count = 0
      })
      this.tickets.forEach(ticket => {
        ticket.count = 0
      })
      this.showResult = false
      this.burningMode = null
      localStorage.removeItem('expCalculatorData')
    },
    resetPotions() {
      this.potions.forEach(potion => {
        potion.count = 0
      })
      this.tickets.forEach(ticket => {
        ticket.count = 0
      })
      this.showResult = false
      this.burningMode = null
    },
    confirmUpgrade() {
      if (!this.isInputValid) return

      this.currentLevel = this.resultLevel
      this.currentExpValue = this.formattedResultExp
      this.currentExpPercentageInput = parseFloat(this.resultPercentage.toFixed(3))
      
      this.potions.forEach(potion => {
        potion.count = 0
      })
      this.tickets.forEach(ticket => {
        ticket.count = 0
      })
      this.showResult = false
      this.saveToLocalStorage()
    },
    saveToLocalStorage() {
      const data = {
        currentLevel: this.currentLevel,
        currentExpValue: this.currentExpValue,
        currentExpPercentageInput: this.currentExpPercentageInput
      }
      localStorage.setItem('expCalculatorData', JSON.stringify(data))
    },
    loadFromLocalStorage() {
      const savedData = localStorage.getItem('expCalculatorData')
      if (savedData) {
        const data = JSON.parse(savedData)
        this.currentLevel = data.currentLevel
        this.currentExpValue = data.currentExpValue
        this.currentExpPercentageInput = data.currentExpPercentageInput
      }
    },
    setBurningMode(mode) {
      // 如果當前等級大於等於260，不允許設置燃燒模式
      if (this.currentLevel >= 260) {
        this.burningMode = null
        return
      }
      
      if (this.burningMode === mode) {
        this.burningMode = null
      } else {
        this.burningMode = mode
      }
      this.showResult = false
    },
    validateTicketCount(index) {
      const ticket = this.tickets[index]
      ticket.count = this.calculator.validateTicketCount(ticket.count)
    },
    canUseTicket(ticketType) {
      if (!this.currentLevel) return false
      return this.calculator.canUseTicket(this.currentLevel, ticketType)
    }
  }
}
</script>

<style scoped>
.exp-calculator {
  max-width: 800px;
  margin: 0 auto;
}

.exp-calculator h1 {
  text-align: left;
  margin-bottom: 2rem;
  color: var(--secondary-color);
}

.calculator-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.input-section {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group h2 {
  font-size: 1.2rem;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
}

.input-group input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

.input-group input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.level-input-container {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.level-input,
.percentage-input {
  flex: 1;
  position: relative;
}

.percentage-input input {
  padding-right: 2rem !important;
}

.percentage-symbol {
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color);
  pointer-events: none;
}

.exp-progress-container {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
}

.exp-progress-bar {
  width: 100%;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.exp-progress-fill {
  height: 100%;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.exp-progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.exp-percentage {
  color: var(--primary-color);
  font-weight: bold;
}

.exp-values {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.exp-value-input {
  width: 200px !important;
  text-align: right;
  padding: 0.3rem 0.5rem !important;
  font-size: 0.9rem !important;
}

.total-exp {
  color: var(--text-color);
}

.error-message {
  color: var(--accent-color);
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.calculate-button,
.reset-button {
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  flex: 1;
}

.calculate-button {
  background-color: var(--nav-bg);
  color: white;
}

.calculate-button:hover:not(.disabled) {
  background-color: var(--nav-hover);
}

.reset-button {
  background-color: #e74c3c;
  color: white;
}

.reset-button:hover:not(.disabled) {
  background-color: #c0392b;
}

.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc !important;
}

.disabled:hover {
  background-color: #ccc !important;
}

.potion-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.potion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.potion-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.potion-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.potion-name {
  color: var(--text-color);
  font-size: 0.9rem;
}

.potion-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.potion-input input {
  width: 100px !important;
  text-align: center;
  padding: 0.3rem 0.5rem !important;
  font-size: 0.9rem !important;
}

.potion-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 4px;
  background-color: var(--nav-bg);
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.potion-btn:hover:not(.disabled) {
  background-color: var(--nav-hover);
}

.potion-btn:active:not(.disabled) {
  background-color: var(--nav-hover);
}

.potion-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc !important;
}

.potion-btn.disabled:hover {
  background-color: #ccc !important;
}

.result-section {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  margin-top: 2rem;
}

.result-section h2 {
  font-size: 1.2rem;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
}

.subtext {
  font-size: 0.9rem;
  color: red;
  margin-bottom: 1rem;
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.result-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.result-value {
  color: var(--primary-color);
  font-size: 1.2rem;
  font-weight: bold;
}

.current-exp {
  color: var(--primary-color);
  font-weight: bold;
}

.reset-level-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  background-color: #e74c3c;
  color: white;
  margin-top: 0;
  margin-left: auto;
}

.reset-level-button:hover {
  background-color: #c0392b;
}

.exp-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.tab-button {
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  background-color: #f8f9fa;
  color: var(--text-color);
}

.tab-button.active {
  background-color: var(--nav-bg);
  color: white;
}

.tab-button:hover:not(.active) {
  background-color: #e9ecef;
}

.ticket-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ticket-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.ticket-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.ticket-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.ticket-details {
  display: flex;
  flex-direction: column;
}

.ticket-name {
  color: var(--text-color);
  font-size: 0.9rem;
}

.ticket-level-range {
  color: var(--text-color);
  font-size: 0.8rem;
}

.ticket-input-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ticket-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ticket-input input {
  width: 150px !important;
  text-align: center;
  padding: 0.5rem !important;
  font-size: 0.9rem !important;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.ticket-input input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.ticket-error {
  color: var(--accent-color);
  font-size: 0.8rem;
  white-space: nowrap;
  min-width: 140px;
}

.burning-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  align-items: center;
  justify-content: space-between;
}

.burning-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 14px;
}

.burning-controls {
  display: flex;
  gap: 0.5rem;
}

.burning-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  background-color: #f8f9fa;
  color: var(--text-color);
}

.burning-button.active {
  background-color: var(--nav-bg);
  color: #fff;
}

.burning-button:hover:not(.active) {
  background-color: #e9ecef;
}

.burning-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc !important;
  color: #666 !important;
}

.burning-button:disabled:hover {
  background-color: #ccc !important;
  color: #666 !important;
}

.burning-notice {
  color: var(--text-color);
  font-size: 0.8rem;
  opacity: 0.7;
  white-space: nowrap;
}

.burning-right {
  display: flex;
  align-items: center;
}

.burning-disabled-notice {
  color: var(--accent-color);
  font-size: 0.8rem;
  opacity: 0.7;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .exp-calculator {
    padding: 1rem;
  }

  .input-section {
    padding: 1.5rem;
  }

  .button-group {
    flex-direction: column;
  }

  .calculate-button,
  .reset-button {
    width: 100%;
  }

  .exp-value-input {
    width: 150px !important;
  }

  .level-input-container {
    flex-direction: column;
    gap: 0.5rem;
  }

  .level-input,
  .percentage-input {
    width: 100%;
  }

  .potion-item {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .potion-info {
    justify-content: center;
  }

  .potion-name {
    text-align: center;
  }

  .potion-input {
    justify-content: center;
  }

  .reset-level-button {
    width: 100%;
    margin-top: 1rem;
  }

  .result-section {
    padding: 1.5rem;
  }

  .burning-buttons {
    flex-direction: column;
    gap: 0.5rem;
    justify-content: flex-start;
    align-items: stretch;
  }
  .burning-button {
    width: 100%;
    margin-right: 0;
  }
}
</style> 